import React from 'react';
import { Link } from 'react-router-dom';
import '../styles/LegalPages.css';

const ShippingDelivery = () => {
  return (
    <div className="legal-page">
      <div className="container">
        <div className="legal-header">
          <Link to="/" className="back-link">← Back to Home</Link>
          <h1>Service Delivery Policy</h1>
          <p className="last-updated">Last Updated: December 2024</p>
        </div>

        <div className="legal-content">
          <section>
            <h2>1. Service Delivery Overview</h2>
            <p>Unlike traditional e-commerce, Sahayak delivers services directly to your location through our network of verified helpers. This policy outlines how our service delivery system works and what you can expect.</p>
            <p><strong>CRITICAL DISCLAIMER:</strong> Sahayak acts solely as a technology platform connecting customers with independent service providers. We do NOT employ helpers, control their actions, or guarantee service quality, punctuality, or completion. All services are provided by independent contractors who are solely responsible for their performance, conduct, and service delivery.</p>
          </section>

          <section>
            <h2>2. Service Areas</h2>
            
            <h3>Currently Available Cities</h3>
            <ul>
              <li>Delhi NCR (Delhi, Gurgaon, Noida, Faridabad, Ghaziabad)</li>
              <li>Mumbai Metropolitan Region</li>
              <li>Bangalore Urban</li>
              <li>Pune</li>
              <li>Hyderabad</li>
              <li>Chennai</li>
              <li>Ahmedabad</li>
              <li>Kolkata</li>
            </ul>

            <h3>Service Radius</h3>
            <ul>
              <li>Helpers typically service within 10-15 km radius of their location</li>
              <li>Some helpers may travel further for longer duration services</li>
              <li>Remote areas may have limited helper availability</li>
              <li>Location verification done during booking process</li>
            </ul>

            <h3>Expanding to New Areas</h3>
            <p>We continuously expand our service areas. If your location isn't currently served, you can:</p>
            <ul>
              <li>Join our waitlist for area expansion updates</li>
              <li>Refer helpers in your area to join our platform</li>
              <li>Contact us for potential priority expansion</li>
            </ul>
          </section>

          <section>
            <h2>3. Service Scheduling</h2>
            
            <h3>Booking Timeline</h3>
            <div className="service-timeline">
              <div className="timeline-item">
                <div className="timeline-icon">⚡</div>
                <div className="timeline-content">
                  <h4>Immediate Booking</h4>
                  <p>Services available within 2-4 hours (subject to helper availability)</p>
                </div>
              </div>
              <div className="timeline-item">
                <div className="timeline-icon">📅</div>
                <div className="timeline-content">
                  <h4>Same Day</h4>
                  <p>Book by 10 AM for same-day service</p>
                </div>
              </div>
              <div className="timeline-item">
                <div className="timeline-icon">🗓️</div>
                <div className="timeline-content">
                  <h4>Advance Booking</h4>
                  <p>Schedule up to 30 days in advance</p>
                </div>
              </div>
            </div>

            <h3>Time Slots</h3>
            <ul>
              <li><strong>Morning:</strong> 8:00 AM - 12:00 PM</li>
              <li><strong>Afternoon:</strong> 12:00 PM - 4:00 PM</li>
              <li><strong>Evening:</strong> 4:00 PM - 8:00 PM</li>
              <li><strong>Custom:</strong> Specific time slots for recurring services</li>
            </ul>

            <h3>Service Duration</h3>
            <ul>
              <li><strong>Minimum Duration:</strong> 2 hours for most services</li>
              <li><strong>Maximum Duration:</strong> 8 hours per day</li>
              <li><strong>Multi-day Services:</strong> Available for extensive projects</li>
              <li><strong>Recurring Services:</strong> Weekly, bi-weekly, or monthly schedules</li>
            </ul>
          </section>

          <section>
            <h2>4. Helper Assignment Process</h2>
            <p><strong>NO GUARANTEE OF ASSIGNMENT:</strong></p>
            <ul>
              <li>Sahayak makes best efforts to find suitable helpers but cannot guarantee assignment</li>
              <li>Helper availability is subject to their individual schedules and preferences</li>
              <li>Sahayak is not responsible if no helpers accept your service request</li>
              <li>Users acknowledge that helper assignment depends on multiple factors beyond Sahayak's control</li>
            </ul>
            
            <h3>Automatic Matching (Best Effort Only)</h3>
            <ol>
              <li><strong>Location Analysis:</strong> Find helpers in your area (availability not guaranteed)</li>
              <li><strong>Service Type:</strong> Match helpers with required skills (skills self-reported by helpers)</li>
              <li><strong>Availability Check:</strong> Confirm helper's schedule (subject to helper confirmation)</li>
              <li><strong>Rating Priority:</strong> Prefer higher-rated helpers (ratings are user opinions, not guarantees)</li>
              <li><strong>Customer Preferences:</strong> Consider language, gender preferences (fulfilled when possible)</li>
            </ol>

            <h3>Manual Selection</h3>
            <ul>
              <li>Browse helper profiles and select preferred helper</li>
              <li>View helper ratings, reviews, and specializations</li>
              <li>Check helper's verified skills and experience</li>
              <li>Send direct booking requests to specific helpers</li>
            </ul>

            <h3>Backup Arrangements</h3>
            <ul>
              <li>If assigned helper cancels, we find replacement within 2 hours</li>
              <li>Alternative helpers suggested with similar ratings</li>
              <li>Option to reschedule if no suitable replacement available</li>
              <li>Full refund if unable to provide service</li>
            </ul>
          </section>

          <section>
            <h2>5. Service Delivery Standards</h2>
            <p><strong>STANDARDS DISCLAIMER:</strong></p>
            <ul>
              <li>Listed standards are target guidelines, not guaranteed commitments</li>
              <li>Sahayak does not control helper behavior, punctuality, or service quality</li>
              <li>All standards depend entirely on individual helper performance</li>
              <li>Sahayak is not liable for deviations from stated standards</li>
            </ul>
            
            <h3>Punctuality (Target Guidelines Only)</h3>
            <ul>
              <li>Helpers target arrival within 30 minutes of scheduled time</li>
              <li>SMS/call notification when helper is en route (when provided by helper)</li>
              <li>Real-time tracking available for premium bookings (subject to helper participation)</li>
              <li>Compensation for delays is at Sahayak's sole discretion and not guaranteed</li>
            </ul>

            <h3>Professional Standards (Helper Responsibility)</h3>
            <ul>
              <li>Standards are helper responsibilities - Sahayak does not guarantee compliance</li>
              <li>Service quality verification through customer feedback (subjective opinions)</li>
              <li>Professional behavior is expected but not guaranteed by Sahayak</li>
              <li>Users engage with helpers entirely at their own risk</li>
            </ul>

            <h3>Safety Protocols (Limited Verification)</h3>
            <ul>
              <li>Background verification is basic and does not guarantee helper trustworthiness</li>
              <li>Identity documents are checked but Sahayak is not responsible for fraudulent documents</li>
              <li>Users are solely responsible for their safety and security during service interactions</li>
              <li>Sahayak provides emergency contact information but is not responsible for emergency response</li>
            </ul>
          </section>

          <section>
            <h2>6. Service Modifications</h2>
            
            <h3>Scope Changes</h3>
            <ul>
              <li><strong>Additional Tasks:</strong> Can be added with helper's consent</li>
              <li><strong>Duration Extension:</strong> Subject to helper availability</li>
              <li><strong>Service Reduction:</strong> Partial refund for unused time</li>
              <li><strong>Billing Adjustment:</strong> Automatic calculation of revised charges</li>
            </ul>

            <h3>Location Changes</h3>
            <ul>
              <li>Address changes allowed up to 2 hours before service</li>
              <li>New location must be within helper's service area</li>
              <li>Additional travel charges may apply</li>
              <li>Helper's consent required for location changes</li>
            </ul>

            <h3>Time Modifications</h3>
            <ul>
              <li><strong>Advance Notice:</strong> Changes allowed up to 4 hours before service</li>
              <li><strong>Emergency Changes:</strong> Subject to helper availability</li>
              <li><strong>No-Charge Modifications:</strong> Once per booking</li>
              <li><strong>Multiple Changes:</strong> Service fee may apply</li>
            </ul>
          </section>

          <section>
            <h2>7. Service Completion</h2>
            <p><strong>COMPLETION DISCLAIMER:</strong></p>
            <ul>
              <li>Service completion is determined by helper and customer agreement</li>
              <li>Sahayak does not verify actual work completion or quality</li>
              <li>Payment release is based on customer confirmation, not Sahayak verification</li>
              <li>Quality disputes are between customer and helper - Sahayak's role is limited to mediation attempts</li>
            </ul>
            
            <h3>Quality Verification (Customer Responsibility)</h3>
            <ul>
              <li>Customer inspects and approves completed work at their own discretion</li>
              <li>Digital work completion photos (when provided) are not verified by Sahayak</li>
              <li>Service completion confirmation through app is customer's responsibility</li>
              <li>24-hour window for quality concerns (resolution not guaranteed)</li>
            </ul>

            <h3>Payment Processing</h3>
            <ul>
              <li><strong>Cash Payment:</strong> Direct payment to helper after completion</li>
              <li><strong>Online Payment:</strong> Automatic release after confirmation</li>
              <li><strong>Dispute Hold:</strong> Payments held during quality investigations</li>
              <li><strong>Tip Option:</strong> Additional gratuity through app</li>
            </ul>

            <h3>Feedback System</h3>
            <ul>
              <li>Rate helper performance (1-5 stars)</li>
              <li>Written reviews to help other customers</li>
              <li>Specific feedback on punctuality, quality, behavior</li>
              <li>Anonymous feedback option available</li>
            </ul>
          </section>

          <section>
            <h2>8. Special Service Delivery</h2>
            
            <h3>Emergency Services</h3>
            <ul>
              <li><strong>Urgent Sweeping & Mopping:</strong> Available within 2 hours</li>
              <li><strong>Emergency Repairs:</strong> Subject to helper expertise</li>
              <li><strong>Event Preparation:</strong> Last-minute assistance</li>
              <li><strong>Priority Fee:</strong> 25% surcharge for emergency requests</li>
            </ul>

            <h3>Bulk Services</h3>
            <ul>
              <li>Multiple helpers assigned for large projects</li>
              <li>Coordinated service delivery for events</li>
              <li>Volume discounts available for regular bookings</li>
              <li>Dedicated project manager for complex requirements</li>
            </ul>

            <h3>Recurring Services</h3>
            <ul>
              <li>Same helper assigned for consistency</li>
              <li>Flexible scheduling based on your routine</li>
              <li>Progressive discounts for long-term commitments</li>
              <li>Priority booking for preferred time slots</li>
            </ul>
          </section>

          <section>
            <h2>9. Service Limitations and Exclusions</h2>
            
            <h3>Services NOT Guaranteed</h3>
            <ul>
              <li>Services requiring professional licenses (customer must verify helper credentials)</li>
              <li>Hazardous material handling (customer assumes all risks)</li>
              <li>Services involving minors without adult supervision (customer responsible for safety)</li>
              <li>Emergency services (Sahayak is not an emergency service provider)</li>
              <li>Services outside designated time slots</li>
            </ul>

            <h3>User Responsibilities and Assumptions of Risk</h3>
            <ul>
              <li><strong>Equipment/Materials:</strong> Customer provides unless specifically agreed (Sahayak not responsible for quality or adequacy)</li>
              <li><strong>Access Requirements:</strong> Customer ensures safe property access and is liable for any access-related issues</li>
              <li><strong>Safety Conditions:</strong> Customer responsible for providing safe working environment</li>
              <li><strong>Communication:</strong> Customer must provide clear instructions (misunderstandings are customer's responsibility)</li>
              <li><strong>Property Security:</strong> Customer responsible for securing valuables and property</li>
            </ul>

            <h3>Maximum Liability Limitation</h3>
            <ul>
              <li>Sahayak's total liability is limited to the platform fees paid for the specific service</li>
              <li>No liability for property damage, theft, personal injury, or consequential damages</li>
              <li>Users waive claims for any damages exceeding the service amount paid</li>
              <li>Time limitations: All claims must be made within 48 hours of service completion</li>
            </ul>
          </section>

          <section>
            <h2>10. Technology Integration</h2>
            
            <h3>Mobile App Features</h3>
            <ul>
              <li>Real-time booking and tracking</li>
              <li>Helper location sharing (with consent)</li>
              <li>In-app communication tools</li>
              <li>Digital payment integration</li>
              <li>Service history and recurring bookings</li>
            </ul>

            <h3>Notifications</h3>
            <ul>
              <li>Booking confirmation and reminders</li>
              <li>Helper assignment notifications</li>
              <li>Service start and completion alerts</li>
              <li>Payment and review reminders</li>
            </ul>
          </section>

          <section>
            <h2>11. Contact for Service Delivery</h2>
            <p>For service delivery questions, modifications, or issues:</p>
            <div className="contact-info">
              <p><strong>Email:</strong> sahayak.platform@gmail.com</p>
            </div>
            <p><strong>IMPORTANT:</strong> Contact does not guarantee resolution of service issues. Sahayak's ability to assist is limited to platform facilitation only.</p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default ShippingDelivery; 